import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * beHuman's own doctors, searchable by name — the list behind "Link to beHuman profile".
 *
 * The deliberate difference from `zocdocPractitionerCandidates`: **nothing has to match.** The
 * candidate list answers "who carries this NPI"; this one answers "who works here", so a Zocdoc
 * provider can be linked to the right person even when Zocdoc reports no NPI, reports a different
 * one, or spells the name differently. The judgement is the operator's.
 *
 * What is *not* relaxed is who is offered. Two exclusions are safety, not tidiness:
 *
 * - the **NPPES referral directory**, whose thousands of external practitioners share the `us-npi`
 *   system but have no login, no organization of ours and no `Schedule`;
 * - **care coordinators**, who have no `Schedule` of their own — a link pointed at one passes
 *   every check and then never books anything.
 *
 * Paged rather than returned whole: the staff list is long enough that a dropdown of everyone is
 * both slow and unreadable.
 */
export const zocdocPractitionerSearch = oc
  .route({ method: 'POST', path: '/v2/zocdoc/practitioner-search' })
  .input(
    z.object({
      /**
       * Free text. Whitespace-separated tokens are matched against name parts; a value containing
       * `@` is treated as an email, which FHIR matches **exactly** rather than by prefix.
       */
      query: z.string().trim().max(200).optional(),
      offset: z.number().int().min(0).default(0),
      count: z.number().int().min(1).max(50).default(20),
    }),
  )
  .output(
    z.object({
      ok: z.boolean(),
      practitioners: z
        .array(
          z.object({
            practitionerId: z.string(),
            name: z.string().optional(),
            /**
             * The practitioner's own NPI. The screen shows it against Zocdoc's so a mismatch is
             * *visible* — it is never a reason to withhold the row, only something the operator
             * should not be able to miss.
             */
            npi: z.string().optional(),
            /** Shown, not filtered on: see the search contract's note on organizations. */
            organizationId: z.string().optional(),
            hasSchedule: z.boolean(),
            /** Already linked to this Zocdoc provider — shown instead of an offer. */
            zocdocProviderId: z.string().optional(),
          }),
        )
        .optional(),
      /** Total matches, so the popup can page rather than guess whether more exist. */
      total: z.number().optional(),
      /** Set when the search itself failed. Distinct from an empty result. */
      error: z.string().optional(),
    }),
  )
